/* eslint @typescript-eslint/ban-types: "off" */
import isArray from 'lodash-es/isArray';
import isObject from 'lodash-es/isObject';
import assign from 'lodash-es/assign';
import * as URI from 'uri-js';

import { get_rpc } from 'cache-store';
import { Parameters, RegistryItem, ServiceDef, ServiceOptions, SMD } from './interfaces';

export const transportRegistry = new Map<string, RegistryItem>();
export const envelopeRegistry = new Map<string, RegistryItem>();

function getTarget(smd: SMD | undefined, method: ServiceDef): string {
	let dest = smd && smd.url ? smd.url : '';
	if (smd && smd.target) {
		if (dest && dest.length > 0) {
			dest = URI.resolve(dest, smd.target);
		} else {
			dest = URI.normalize(smd.target);
		}
	}
	if (method.target) {
		dest = smd && smd.url ? smd.url : '';
		if (dest && dest.length > 0) {
			dest = URI.resolve(dest, method.target);
		} else {
			dest = URI.normalize(method.target);
		}
	}
	return dest;
}

export class RpcService {
	_smd?: SMD;
	_options: ServiceOptions;
	_requestId: number;
	_envDef: any;
	static _nextId = 0;
	[index: string]: any;

	constructor(smd?: SMD, options: ServiceOptions = {}) {
		this._requestId = 0;
		this._options = options;

		const processSMD = (smd: SMD): void => {
			this._smd = smd;
			for (const servicename in smd.services) {
				// allow namespaced services
				const pieces = servicename.split('.');
				// eslint-disable-next-line @typescript-eslint/no-this-alias
				let current: any = this;
				for (let i = 0; i < pieces.length - 1; i++) {
					current = current[pieces[i]] || (current[pieces[i]] = {});
				}
				current[pieces[pieces.length - 1]] = this._generateService(servicename, smd.services[servicename]);
			}
		};

		smd && processSMD(smd);
	}

	private _generateService(servicename: string, method: ServiceDef) {
		if (this[servicename]) {
			throw new Error(`WARNING: ${servicename} already exists for service. Unable to generate function`);
		}
		method.name = servicename;
		let func: Function = this._executeMethod.bind(this, method);
		const transportName = method.transport || (this._smd && this._smd.transport);
		if (transportName !== undefined) {
			const transport = transportRegistry.get(transportName);
			if (transport && transport.getExecutor) {
				func = transport.getExecutor(func, method, this);
			}
		}
		const schema = (method as any).returns || ((method as any)._schema = {});
		const servicepath = `/${servicename}/`;
		schema._service = func;
		(func as any).servicepath = servicepath;
		(func as any)._schema = schema;
		(func as any).id = RpcService._nextId++;
		return func as Function;
	}

	private _getRequest(method: ServiceDef, ...args: any[]) {
		const smd = this._smd;
		const envdef = envelopeRegistry.get(method.envelope || (smd && smd.envelope) || 'NONE');
		const parameters = (method.parameters || []).concat((smd && smd.parameters) || []) as Parameters[];
		let argvals: any;
		if (envdef && envdef.namedParams) {
			if (isArray(args[0])) {
				args = args[0];
			}
			if (args.length === 1 && isObject(args[0])) {
				argvals = args[0];
			} else {
				const data: { [key: string]: any } = {};
				for (let i = 0; i < method.parameters.length; i++) {
					const params = method.parameters[i];
					if (params.name && (typeof args[i] !== 'undefined' || !params.optional)) {
						data[params.name] = args[i];
					}
				}
				argvals = data;
			}
			if (method.strictParameters || (smd && smd.strictParameters)) {
				for (const i in argvals) {
					let found = false;
					for (let j = 0; j < parameters.length; j++) {
						if (parameters[j].name === i) {
							found = true;
						}
					}
					if (!found) {
						delete argvals[i];
					}
				}
			}
			// Set default values.
			for (let i = 0; i < parameters.length; i++) {
				const param = parameters[i];
				if (!param.optional && param.name && !argvals[param.name]) {
					if (param['default']) {
						argvals[param.name] = param['default'];
					} else if (!(param.name in argvals)) {
						throw new Error(`Required parameter ${param.name} was omitted`);
					}
				}
			}
		} else if (parameters && parameters[0] && parameters[0].name && args.length === 1 && isObject(args[0])) {
			if (envdef && envdef.namedParams === false) {
				// unimplemented
			} else {
				// unimplemented
			}
		}

		const schema = method._schema || method.returns;
		const request: { [key: string]: any } = envdef!.serialize!.apply(this, [smd!, method, argvals]);
		request._envDef = envdef;
		const contentType = method.contentType || (smd && smd.contentType) || request.contentType;
		const settings = {
			sync: !!this._options.sync,
			contentType: contentType,
			headers: method.headers || (smd && smd.headers) || request.headers || {},
			target: request.target || getTarget(smd, method),
			transport: method.transport || (smd && smd.transport) || request.transport,
			envelope: method.envelope || (smd && smd.envelope) || request.envelope,
			timeout: method.timeout || (smd && smd.timeout),
			callbackParamName: method.callbackParamName || (smd && smd.callbackParamName),
			rpcObjectParamName: method.rpcObjectParamName || (smd && smd.rpcObjectParamName),
			schema: schema,
			handleAs: request.handleAs || 'auto',
			preventCache: method.preventCache || (smd && smd.preventCache),
			cacheable: method.cacheable || (smd && smd.cacheable),
			cacheLifetime: method.cacheLifetime || (smd && smd.cacheLifetime),
			returnResponseObject: method.returnResponseObject || (smd && smd.returnResponseObject),
			frameDoc: this._options.frameDoc || undefined
		};
		return assign(request, settings as any) as ServiceDef;
	}

	private async _executeMethod(method: ServiceDef, ...args: any[]) {
		const request = this._getRequest(method, args);
		const action = () => transportRegistry.get(request.transport as string)!.fire!(request);
		const promise = get_rpc({ request, action });
		return promise.then(
			(results: any) => request._envDef!.deserialize!.call(this, results),
			(results: any) => request._envDef!.deserialize!.call(this, results)
		);
	}
}
